import React, { useState, useEffect, useCallback } from 'react';
import ConversationList from './ConversationList';
import ChatWindow from './ChatWindow';
import { chatApi, connectChatSocket, disconnectChatSocket, getChatSocket } from '../../Services/chatService';
import './Chat.css';

const ChatLayout = () => {
    const [conversations, setConversations]         = useState([]);
    const [selectedConv, setSelectedConv]           = useState(null);
    const [agents, setAgents]                       = useState([]);
    const [loading, setLoading]                     = useState(true);
    const [statusFilter, setStatusFilter]           = useState('');
    const [search, setSearch]                       = useState('');

    const admin = JSON.parse(localStorage.getItem('admin') || '{}');
    const role  = admin.role || '';

    // ── Fetch conversations ───────────────────────────────────────────────────
    const fetchConversations = useCallback(async () => {
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (search)       params.search = search;
            const { data } = await chatApi.getConversations(params);
            if (data.status === 1) setConversations(data.data.conversations);
        } catch (e) {
            console.error('fetchConversations:', e);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search]);

    // ── Fetch agents ──────────────────────────────────────────────────────────
    const fetchAgents = useCallback(async () => {
        try {
            const { data } = await chatApi.getAgents();
            if (data.status === 1) setAgents(data.data);
        } catch (e) { /* silent */ }
    }, []);

    // ── Socket.IO setup ───────────────────────────────────────────────────────
    useEffect(() => {
        const socket = connectChatSocket();
        if (socket) {
            // Live updates to conversation list
            socket.on('conversation_updated', (update) => {
                setConversations(prev =>
                    prev.map(c => c._id === update.conversationId
                        ? { ...c, ...update } : c)
                );
            });
            socket.on('chat_assigned', ({ conversation }) => {
                setConversations(prev => {
                    const exists = prev.find(c => c._id === conversation._id);
                    return exists
                        ? prev.map(c => c._id === conversation._id ? conversation : c)
                        : [conversation, ...prev];
                });
            });
            socket.on('conversation_closed', ({ conversationId }) => {
                setConversations(prev =>
                    prev.map(c => c._id === conversationId
                        ? { ...c, status: 'closed' } : c)
                );
            });
            socket.on('agent_presence', ({ agentId, status }) => {
                setAgents(prev =>
                    prev.map(a => a._id === agentId
                        ? { ...a, isOnline: status === 'online' } : a)
                );
            });
        }

        fetchConversations();
        if (['admin', 'subadmin', 'supervisor'].includes(role)) fetchAgents();

        // Refresh list every 30s
        const interval = setInterval(fetchConversations, 30000);
        return () => {
            clearInterval(interval);
            disconnectChatSocket();
        };
    }, [fetchConversations, fetchAgents, role]);

    const handleSelectConversation = (conv) => {
        setSelectedConv(conv);
        // Mark unread as 0 locally
        setConversations(prev =>
            prev.map(c => c._id === conv._id ? { ...c, unreadCountAdmin: 0 } : c)
        );
    };

    const handleConvUpdate = (update) => {
        setConversations(prev =>
            prev.map(c => c._id === update._id ? { ...c, ...update } : c)
        );
        if (selectedConv && selectedConv._id === update._id) {
            setSelectedConv(prev => ({ ...prev, ...update }));
        }
    };

    return (
        <div className={`chat-layout ${selectedConv ? 'conv-selected' : ''}`}>
            {/* Left Panel — Conversation List */}
            <div className="chat-sidebar">
                <div className="chat-sidebar-header">
                    <h2 className="chat-sidebar-title">
                        <span className="chat-icon">💬</span> Live Support
                    </h2>
                    <div className="chat-filters">
                        <input
                            type="text"
                            className="chat-search"
                            placeholder="Search customer..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <select
                            className="chat-filter-select"
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            <option value="">All</option>
                            <option value="waiting">Waiting</option>
                            <option value="open">Open</option>
                            <option value="closed">Closed</option>
                        </select>
                    </div>
                </div>
                <ConversationList
                    conversations={conversations}
                    selectedId={selectedConv?._id}
                    onSelect={handleSelectConversation}
                    loading={loading}
                />
            </div>

            {/* Right Panel — Chat Window */}
            <div className="chat-main">
                {selectedConv ? (
                    <ChatWindow
                        conversation={selectedConv}
                        agents={agents}
                        role={role}
                        onConvUpdate={handleConvUpdate}
                        onBack={() => setSelectedConv(null)}
                    />
                ) : (
                    <div className="chat-empty-state">
                        <div className="chat-empty-icon">💬</div>
                        <h3>Select a conversation</h3>
                        <p>Choose a customer chat from the left panel to start supporting</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatLayout;
